import React, { useCallback, useLayoutEffect, useEffect, useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { useSelector, useDispatch } from 'react-redux';
import { notification, Switch } from 'antd';
import { translateParts } from '@/utils/i18n';

import { Header } from './Header';
import { Content } from './Content';
import { MenuOptions } from './Menu';
import Api from '@/services/api';
import { Sider } from './Sider';
import { setPlanDetails } from '@/redux/plan/actions';
import { ModalSandbox } from '@/components/ui/Modals/ModalSandbox';
import { useApplicationContext } from '@/context/v1/Application/context';

import * as S from './styles';

interface ContainerProps {
	children: () => JSX.Element;
}

export function Container({ children }: Readonly<ContainerProps>) {
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const MODAL_MESSAGE_EXIT_ENVIRONMENT = {
		production: translateParts([
			'You are activating sandbox mode.',
			'From now on, no actions will be recorded in the official environment.',
			'Use the area for simulations.'
		]),
		sandbox: translateParts([
			'You are exiting sandbox mode.',
			'All actions that have not been applied will be lost.'
		])
	};

	const dispatch = useDispatch();
	const isExpired = useSelector((state: any) => state.plan.expired);
	const isCanceled = useSelector((state: any) => state.plan.canceled);
	const { isSandbox, siderCollapsed, handleSiderCollapse, toggleSandbox } = useApplicationContext();

	const getPlan = useCallback(() => {
		Api.get('/plan/customer/details')
			.then((res) => {
				dispatch(setPlanDetails(res.data));
			})
			.catch((err) => {
				console.log(err, 'data');
			});
	}, [dispatch]);

	const alertAboutPlan = useCallback(() => {
		if (isCanceled) {
			const message = I18n.get('Your plan has been canceled');
			const description = I18n.get('Upgrade your plan and continue using one platform');
			notification.error({ message, description });
		}

		if (isExpired) {
			const message = I18n.get('Your plan has expired');
			const description = I18n.get('Renew it to continue using');
			notification.warning({ message, description });
		}
	}, [isExpired, isCanceled]);

	useLayoutEffect(() => {
		getPlan();
	}, [getPlan]);

	useEffect(() => {
		alertAboutPlan();
		handleSiderCollapse(window.innerWidth < 1200);
	}, [alertAboutPlan]);

	function handleOpenModal() {
		const message = isSandbox ? MODAL_MESSAGE_EXIT_ENVIRONMENT.sandbox : MODAL_MESSAGE_EXIT_ENVIRONMENT.production;
		const primaryButton = {
			style: {
				backgroundColor: isSandbox ? '#FAEBEC' : '#2f54eb',
				border: isSandbox ? '0.0625rem solid #D13342' : '0.0625rem solid #2f54eb',
				color: isSandbox ? '#D13342' : '#fff'
			},
			text: isSandbox ? I18n.get('Exit without applying') : I18n.get('Continue')
		};

		const secondaryButton = {
			style: {
				border: '0.0625rem solid #e6e6e6'
			},
			text: I18n.get('Cancel')
		};

		const onCancel = () => {
			setIsModalOpen(false);
		};

		const onConfirm = () => {
			toggleSandbox(!isSandbox);
			setIsModalOpen(false);
		};
		return (
			<ModalSandbox
				open={isModalOpen}
				onCancel={onCancel}
				onConfirm={onConfirm}
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
			>
				{message}
			</ModalSandbox>
		);
	}

	function handleSandboxMode() {
		setIsModalOpen(true);
	}

	const tooltipMessage = isSandbox ? I18n.get('To production') : I18n.get('To sandbox');

	return (
		<>
			<Header />
			<S.CustomLayout>
				<Sider>
					<S.SiderMenuContainer>
						<S.SiderMenuContainerDivider>
							<MenuOptions />
						</S.SiderMenuContainerDivider>
						<S.SiderMenuContainerDividerSandbox>
							<S.SandboxTooltip title={tooltipMessage} siderCollapsed={siderCollapsed}>
								<Switch checked={isSandbox} onChange={() => handleSandboxMode()} />{' '}
								<S.SandboxLabel>Sandbox</S.SandboxLabel>
							</S.SandboxTooltip>
						</S.SiderMenuContainerDividerSandbox>
					</S.SiderMenuContainer>
				</Sider>
				<S.LayoutContent $siderCollapsed={siderCollapsed}>
					<Content>{children}</Content>
				</S.LayoutContent>
			</S.CustomLayout>
			{handleOpenModal()}
		</>
	);
}
